import React from "react";

import { EditorState } from "draft-js";
import { Col, Row, Tooltip } from "antd";
import {
    FormatListBulleted,
    FormatListNumbered,
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    FormatAlignLeft,
    FormatAlignCenter,
    FormatAlignRight,
    FormatAlignJustify,
} from "@mui/icons-material";
import IconButton from "../IconButton";

interface IControlsProps {
    editorState: EditorState;
    handlers: {
        toggleBlockType: (blockType: string) => void;
        toggleInlineStyle: (inlineStyle: string) => void;
        toggleAlignment: (alignmentStyle: string) => void;
    };
}

/**
 * Defines the structure for all control block types used in the editor.
 */
interface IControlTypeDefinition {
    label: string;
    style: string;
    icon?: React.ReactNode;
}

/**
 * Available block types for the editor.
 */
const BLOCK_TYPES: IControlTypeDefinition[] = [
    { label: "H1", style: "header-one" },
    { label: "H2", style: "header-two" },
    { label: "H3", style: "header-three" },
    { label: "UL", style: "unordered-list-item", icon: <FormatListBulleted /> },
    { label: "OL", style: "ordered-list-item", icon: <FormatListNumbered /> },
];

/**
 * Available inline styles for the editor.
 */
const INLINE_STYLES: IControlTypeDefinition[] = [
    { label: "Bold", style: "BOLD", icon: <FormatBold /> },
    { label: "Italic", style: "ITALIC", icon: <FormatItalic /> },
    { label: "Underline", style: "UNDERLINE", icon: <FormatUnderlined /> },
];

/**
 * Available alignment styles for the editor.
 */
const ALIGNMENT_STYLES: IControlTypeDefinition[] = [
    { label: "Left", style: "left", icon: <FormatAlignLeft /> },
    { label: "Center", style: "center", icon: <FormatAlignCenter /> },
    { label: "Right", style: "right", icon: <FormatAlignRight /> },
    { label: "Justify", style: "justify", icon: <FormatAlignJustify /> },
];

/**
 * Props for the BlockStyleControls component.
 */
interface IControlProps {
    editorState: EditorState;
    onToggle: (blockType: string) => void;
}

// ** Sub Components ** //

/**
 * Component for rendering block style control buttons.
 */
const BlockStyleControls = ({ editorState, onToggle }: IControlProps) => {
    const selection = editorState.getSelection();
    const blockType = editorState.getCurrentContent().getBlockForKey(selection.getStartKey()).getType();

    return (
        <div className="rich-text-editor-controls">
            {BLOCK_TYPES.map((type) => (
                <Tooltip key={type.label} title={type.label}>
                    <IconButton
                        icon={type.icon}
                        className={type.style === blockType ? "dark" : "secondary"}
                        onMouseDown={(event) => {
                            event.preventDefault();
                            onToggle(type.style);
                        }}
                        style={{ marginRight: 8 }}
                    >
                        {!type.icon && type.label}
                    </IconButton>
                </Tooltip>
            ))}
        </div>
    );
};

/**
 * Component for rendering inline style control buttons.
 */
const InlineStyleControls = ({ editorState, onToggle }: IControlProps) => {
    const currentStyle = editorState.getCurrentInlineStyle();
    return (
        <div className="rich-text-editor-controls">
            {INLINE_STYLES.map((type) => (
                <Tooltip key={type.label} title={type.label}>
                    <IconButton
                        icon={type.icon}
                        className={currentStyle.has(type.style) ? "dark" : "secondary"}
                        onMouseDown={(event) => {
                            event.preventDefault();
                            onToggle(type.style);
                        }}
                        style={{ marginRight: 8 }}
                    />
                </Tooltip>
            ))}
        </div>
    );
};

/**
 * Component for rendering alignment control buttons.
 */
const AlignmentControls = ({ editorState, onToggle }: IControlProps) => {
    const selection = editorState.getSelection();
    const currentAlignment = editorState
        .getCurrentContent()
        .getBlockForKey(selection.getStartKey())
        .getData()
        .get("textAlign") as string;

    return (
        <div className="rich-text-editor-controls">
            {ALIGNMENT_STYLES.map((type) => {
                const isDefault = type.style === "left" && currentAlignment === undefined;
                return (
                    <Tooltip key={type.label} title={type.label}>
                        <IconButton
                            icon={type.icon}
                            className={type.style === currentAlignment || isDefault ? "dark" : "secondary"}
                            onMouseDown={(event) => {
                                event.preventDefault();
                                onToggle(type.style);
                            }}
                            style={{ marginRight: 8 }}
                        />
                    </Tooltip>
                );
            })}
        </div>
    );
};

/**
 * Component for rendering all control buttons for the Rich Text Editor.
 */
const Controls = ({ editorState, handlers }: IControlsProps) => (
    <Row>
        <Col>
            <BlockStyleControls editorState={editorState} onToggle={handlers.toggleBlockType} />
        </Col>
        <Col>
            <InlineStyleControls editorState={editorState} onToggle={handlers.toggleInlineStyle} />
        </Col>
        <Col>
            <AlignmentControls editorState={editorState} onToggle={handlers.toggleAlignment} />
        </Col>
    </Row>
);

export default Controls;
